"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";
import { useRouter } from "next/navigation";

type LoginErrorStatus =
  | "pending_email_verification"
  | "suspended"
  | "blocked"
  | null;

type LoginResponse = {
  accessToken?: string;
  user?: {
    id: string;
    name: string;
    username?: string | null;
    email: string;
    userType: "user" | "admin";
    status: string;
  };
  message?: string | string[];
  status?: string;
  error?: string;
};

type ResendVerificationResponse = {
  message?: string | string[];
};

export default function LoginPage() {
  const router = useRouter();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loginStatus, setLoginStatus] = useState<LoginErrorStatus>(null);


  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setLoginStatus(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ login, password }),
      });

      const data = (await response.json()) as LoginResponse;

      if (!response.ok) {
        const message = Array.isArray(data.message)
          ? data.message.join(", ")
          : data.message || "Não foi possível realizar o login.";

        const returnedStatus =
          data.status === "pending_email_verification" ||
          data.status === "suspended" ||
          data.status === "blocked"
            ? data.status
            : null;

        setLoginStatus(returnedStatus);
        setErrorMessage(message);
        return;
      }

      localStorage.setItem("platform_access_token", data.accessToken || "");
      localStorage.setItem("platform_user", JSON.stringify(data.user));

      setSuccessMessage("Login realizado com sucesso.");

      if (data.user?.userType === "admin") {
        router.push("/admin/painel");
      } else {
        router.push("/painel");
      }
    } catch {
      setErrorMessage("Erro de conexão com a API.");
    } finally {
      setLoading(false);
    }
  }

async function handleResendVerification() {
  setResendingVerification(true);
  setErrorMessage("");
  setSuccessMessage("");

  try {
    const normalizedLogin = login.trim().toLowerCase();

    if (!normalizedLogin) {
      setErrorMessage("Informe seu e-mail ou username para reenviar a verificação.");
      return;
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/auth/resend-verification`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ login: normalizedLogin }),
      },
    );

    const data = (await response.json()) as ResendVerificationResponse;

    if (!response.ok) {
      setErrorMessage(
        Array.isArray(data.message)
          ? data.message.join(", ")
          : data.message || "Não foi possível reenviar o e-mail de verificação.",
      );
      return;
    }

    setSuccessMessage(
      Array.isArray(data.message)
        ? data.message.join(", ")
        : data.message || "Novo e-mail de verificação enviado com sucesso.",
    );
  } catch {
    setErrorMessage("Erro de conexão com a API.");
  } finally {
    setResendingVerification(false);
  }
}

  function renderStatusHelp() {
    if (loginStatus === "pending_email_verification") {
      return (
        <div className="rounded-2xl border border-amber-500/25 bg-[linear-gradient(180deg,rgba(245,158,11,0.10),rgba(245,158,11,0.06))] p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-xl">
              <div className="text-base font-semibold text-amber-200">
                Confirme seu e-mail para liberar o acesso
              </div>

              <p className="mt-2 text-sm leading-6 text-amber-100/80">
                Seu cadastro foi criado com sucesso, mas a conta ainda precisa ser ativada
                pelo link enviado para o seu e-mail.
              </p>

              <p className="mt-2 text-xs leading-5 text-amber-100/65">
                Você pode reenviar usando o mesmo e-mail ou username informado no login.
              </p>
            </div>

            <div className="shrink-0">
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingVerification}
                className="inline-flex items-center justify-center rounded-xl border border-amber-300/25 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-100 transition hover:bg-amber-400/20 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {resendingVerification ? "Reenviando..." : "Reenviar verificação"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (loginStatus === "suspended") {
      return (
        <div className="rounded-2xl border border-orange-500/25 bg-[linear-gradient(180deg,rgba(249,115,22,0.10),rgba(249,115,22,0.06))] p-5">
          <div className="text-base font-semibold text-orange-200">
            Sua conta está inativa no momento
          </div>

          <p className="mt-2 max-w-xl text-sm leading-6 text-orange-100/80">
            Este acesso está temporariamente indisponível. Se precisar reativar sua conta,
            entre em contato com o suporte.
          </p>

          <div className="mt-4">
            <Link
              href="/suporte"
              className="inline-flex items-center justify-center rounded-xl border border-orange-300/25 bg-orange-400/10 px-4 py-2.5 text-sm font-semibold text-orange-100 transition hover:bg-orange-400/20"
            >
              Falar com o suporte
            </Link>
          </div>
        </div>
      );
    }
    
    if (loginStatus === "blocked") {
      return (
        <div className="rounded-2xl border border-red-500/25 bg-[linear-gradient(180deg,rgba(239,68,68,0.10),rgba(239,68,68,0.06))] p-5">
          <div className="text-base font-semibold text-red-200">
            Sua conta está bloqueada
          </div>

          <p className="mt-2 max-w-xl text-sm leading-6 text-red-100/80">
            Este acesso está bloqueado no momento. Se você acredita que isso é um engano,
            fale com o suporte para análise.
          </p>

          <div className="mt-4">
            <Link
              href="/suporte"
              className="inline-flex items-center justify-center rounded-xl border border-red-300/25 bg-red-400/10 px-4 py-2.5 text-sm font-semibold text-red-100 transition hover:bg-red-400/20"
            >
              Entrar em contato
            </Link>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div>
      <div className="mb-8">
        <div className="platform-text-muted text-sm">Acesso à plataforma</div>

        <h1 className="platform-text mt-3 text-4xl font-semibold tracking-tight">
          Entrar na Platform
        </h1>

        <p className="platform-text-secondary mt-4 text-base leading-7">
          Acesse sua conta para entrar no painel da plataforma.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="platform-text-secondary mb-2 block text-sm font-medium">
            E-mail ou username
          </label>
          <input
            type="text"
            required
            autoComplete="username"
            placeholder="voce@exemplo.com ou seuusuario"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className="platform-input"
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="platform-text-secondary block text-sm font-medium">
              Senha
            </label>

            <Link href="/recuperar-senha" className="platform-link text-sm">
              Esqueci minha senha
            </Link>
          </div>

          <input
            type="password"
            required
            autoComplete="current-password"
            placeholder="Digite sua senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="platform-input"
          />
        </div>

        {errorMessage && !loginStatus ? (
          <div className="platform-alert-error">
            {errorMessage}
          </div>
        ) : null}

        {renderStatusHelp()}

        {successMessage ? (
          <div className="platform-alert-success">
            {successMessage}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="platform-button-primary"
        >
          {loading ? "Autenticando..." : "Entrar"}
        </button>
      </form>

      <div className="platform-divider platform-text-secondary mt-8 pt-6 text-sm">
        Ainda não tem conta?{" "}
        <Link href="/cadastro" className="platform-text transition">
          Criar conta
        </Link>
      </div>
    </div>
  );
}